import type { StoryWithUIState } from "../../types";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { StoryCard } from "./StoryCard";
import { StoryTableHeader, StoryTableRow } from "./StoryTableRow";

interface StoriesContentProps {
    stories: StoryWithUIState[];
    sortBy: "updated_at" | "topic";
    sortOrder: "asc" | "desc";
    onSortChange: (sortBy: "updated_at" | "topic") => void;
    onStoryClick: (storyId: string) => void;
    onDelete: (story: { id: string; title?: string; topic: string }) => void;
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

export function StoriesContent({
    stories,
    sortBy,
    sortOrder,
    onSortChange,
    onStoryClick,
    onDelete,
    page,
    limit,
    total,
    totalPages,
    onPageChange,
}: StoriesContentProps) {
    return (
        <>
            <Card className="hidden md:block">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <StoryTableHeader sortBy={sortBy} sortOrder={sortOrder} onSortChange={onSortChange} />
                        <tbody>
                            {stories.map((story) => (
                                <StoryTableRow
                                    key={story.id}
                                    story={story}
                                    onStoryClick={onStoryClick}
                                    onDelete={onDelete}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            <div className="md:hidden space-y-4">
                {stories.map((story) => (
                    <StoryCard key={story.id} story={story} onStoryClick={onStoryClick} onDelete={onDelete} />
                ))}
            </div>

            {totalPages > 1 && (
                <Card className="p-4">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="text-sm text-muted-foreground text-center sm:text-left">
                            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} stories
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onPageChange(Math.max(1, page - 1))}
                                disabled={page <= 1}
                                className="hover:text-primary hover:border-primary"
                            >
                                Previous
                            </Button>
                            <span className="text-sm px-3 py-1">
                                Page {page} of {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                                disabled={page >= totalPages}
                                className="hover:text-primary hover:border-primary"
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                </Card>
            )}
        </>
    );
}
